import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { API_URL, getAuthHeader } from '@/lib/utils';
import { Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [activeTab, setActiveTab] = useState('client');
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website: '',
    type: 'client',
    is_active: true
  });

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/partners`);
      setPartners(response.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  const resetForm = () => {
    setFormData({ name: '', logo_url: '', website: '', type: activeTab, is_active: true });
    setEditingPartner(null);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      logo_url: partner.logo_url,
      website: partner.website || '',
      type: partner.type || 'client',
      is_active: partner.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.logo_url) {
      toast.error('Name and logo are required');
      return;
    }
    try {
      if (editingPartner) {
        await axios.put(`${API_URL}/partners/${editingPartner.id}`, formData, { headers: getAuthHeader() });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API_URL}/partners`, formData, { headers: getAuthHeader() });
        toast.success('Created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/partners/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filtered = partners.filter(p => (p.type || 'client') === activeTab);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-partners">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-[#1a2a5e]">Clients & Partners</h1>
          <p className="text-gray-500 mt-1">Manage client logos and partner organizations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1a2a5e] hover:bg-[#2d4a8e]" onClick={() => setFormData(f => ({ ...f, type: activeTab }))}>
              <Plus className="mr-2 h-4 w-4" />
              Add {activeTab === 'client' ? 'Client' : 'Partner'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingPartner ? 'Edit' : 'Add'} {formData.type === 'client' ? 'Client' : 'Partner'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Organization name"
                  data-testid="partner-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <ImageUpload
                value={formData.logo_url}
                onChange={(url) => setFormData({ ...formData, logo_url: url })}
                label="Logo *"
              />

              <div className="space-y-2">
                <Label>Website URL</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#1a2a5e]">
                  {editingPartner ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="client">Our Clients ({partners.filter(p => (p.type || 'client') === 'client').length})</TabsTrigger>
          <TabsTrigger value="partner">Partners ({partners.filter(p => p.type === 'partner').length})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Logo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Website</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <img src={item.logo_url} alt={item.name} className="h-10 w-20 object-contain rounded" />
                      </TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>
                        {item.website ? (
                          <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-[#1a2a5e] hover:underline flex items-center gap-1 text-sm">
                            Visit <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No {activeTab === 'client' ? 'clients' : 'partners'} yet. Add your first one!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPartners;
